import { getTopWhtPaymentCategories } from '@/api/dashboardApi'
import HorizontalGraph from '@/components/dashboard/HorizontalGraph'
import { topPaymentCategoriesUIMetaData } from '@/constants/dashboardConstant'
import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// ==================== Helpers ====================
const mapTopWhtPaymentCategoriesToItems = (data = []) => {
  if (!Array.isArray(data)) {
    return []
  }
  return data?.map(category => ({
    label: category?.paymentCategory,
    expenseValue: category?.expenseLiableToWhtValue,
    whtValue: category?.whtAmountValue,
    expenseDisplay: category?.expenseLiableToWht,
    whtDisplay: category?.whtAmount,
  }))
}

// Builds the graph footnote from the API's valueConversion (e.g. "millions")
// and currencyCode (e.g. "USD"). Returns the provided fallback when either
// value is missing so the UI never shows a partial note.
const buildValuesNote = (valueConversion, currencyCode, fallback) => {
  if (!valueConversion || !currencyCode) {
    return fallback
  }
  const unit =
    valueConversion.charAt(0).toUpperCase() + valueConversion.slice(1)
  return `*All values are in ${unit} & ${currencyCode}`
}

const TopWhtPaymentCategoriesGraph = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [items, setItems] = useState([])
  const [note, setNote] = useState(topPaymentCategoriesUIMetaData.note)
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const paymentCategoriesResponse =
          await getTopWhtPaymentCategories(appliedFilters)
        processData(paymentCategoriesResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Component Helpers ====================
  const processData = paymentCategoriesResponse => {
    const paymentCategoriesData =
      paymentCategoriesResponse?.data?.data?.paymentCategories
    if (paymentCategoriesResponse?.success && paymentCategoriesData) {
      setItems(mapTopWhtPaymentCategoriesToItems(paymentCategoriesData))
      setNote(
        buildValuesNote(
          paymentCategoriesResponse?.data?.data?.valueConversion,
          paymentCategoriesResponse?.data?.data?.currencyCode,
          topPaymentCategoriesUIMetaData.note
        )
      )
    } else {
      setItems([])
      setNote(topPaymentCategoriesUIMetaData.note)
    }
  }

  // ==================== Return ====================
  if (!dataLoaded) {
    return (
      <Box
        sx={{
          border: '1px solid var(--border-subtel)',
          backgroundColor: 'var(--white-color)',
          p: 2,
          flex: 1,
          minWidth: 300,
          minHeight: 500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            color: 'var(--forbrand-primary)',
            fontSize: 'var(--font-size-lg)',
          }}
        >
          Loading...
        </Typography>
      </Box>
    )
  }

  return (
    <HorizontalGraph
      heading={topPaymentCategoriesUIMetaData.heading}
      legend={topPaymentCategoriesUIMetaData.legend}
      note={note}
      items={items}
    />
  )
}

export default TopWhtPaymentCategoriesGraph
